import {
  addDoc,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  Timestamp,
} from "firebase/firestore";
import { db } from "../../firebase/config";
import { copyMenu, listStoreMenus } from "./menuCopy";
import { createKitchenAllPrinter, createMevPrinterSetting } from "./builtinPrinters";

const DROP_KEYS = new Set([
  "id",
  "printerSettings",
  "mevPrinters",
  "stripeCustomerId",
  "stripeSubscriptionId",
  "subscriptionStartDate",
  "subscriptionEndDate",
  "trialEndDate",
  "limitFirstReachedDate",
  "organization",
  "admins",
  "members",
  "invitations",
  "kdsUsers",
  "createdBy",
  "linkedPOSUserByEmail",
  "permissions",
  "userSignInOptions",
  "storeAccess",
  "users",
  "maxStations",
  "activeStationCount",
  "created",
  "updated",
  "storeBaseUrl",
  "productionCutoff",
  "storeMode",
  "cloudMevEnvirn",
]);

export type OrganizationChoice =
  | { mode: "skip" }
  | { mode: "existing"; type: "headquarters" | "legalEntity" | "brand" | "operator"; id: string }
  | {
      mode: "create";
      type: "headquarters" | "legalEntity" | "brand" | "operator";
      name: string;
      address?: string;
      phone?: string;
      email?: string;
    };

export type BillingSubactivity = "RES" | "BAR" | "CDR";

export function billingSubactivityOf(value: unknown): BillingSubactivity {
  return value === "BAR" || value === "CDR" ? value : "RES";
}

export type NewStoreInput = {
  ownerEmail: string;
  name: string;
  billingSubactivity?: BillingSubactivity;
  address: string;
  phone: string;
  email: string;
  currency: string;
  latitude: string;
  longitude: string;
  adminFirstName: string;
  adminLastName: string;
  adminEmail: string;
  managerFirstName: string;
  managerLastName: string;
  managerEmail: string;
  sourceStoreId: string;
  organization: OrganizationChoice;
};

export type CreatedStorePins = {
  storeId: string;
  storeName: string;
  ownerPin: string;
  ownerName: string;
  ownerEmail: string;
  adminPin: string;
  adminName: string;
  adminEmail: string;
  managerPin: string;
  managerName: string;
  managerEmail: string;
  menuWarning: string;
};

const FIELD_BY_TYPE = {
  headquarters: "headquartersId",
  legalEntity: "legalEntityId",
  brand: "brandId",
  operator: "operatorId",
} as const;

const adminPermissions = {
  sidebar: { pos: true, kds: true, store: true, reservations: true, customers: true, menus: true, analytics: true, layout: true, settings: true, mev: true },
  settingsTabs: { storeDetails: true, storeOperation: true, menu: true, order: true, payment: true, print: true, webhooks: true, users: true, others: true, tables: true, website: true, cloudSync: true },
  settingsCards: {},
  posOperations: { createOrders: true, completeOrders: true, processRefunds: true, viewReports: true, manageTables: true, manageReservations: true, manageCustomers: true, managePOSUsers: true, endOfDay: true },
};

const memberPermissions = {
  sidebar: { pos: true, kds: false, store: false, reservations: false, customers: false, menus: false, analytics: false, layout: false, settings: false, mev: false },
  settingsTabs: { storeDetails: false, storeOperation: false, menu: false, order: false, payment: false, print: false, webhooks: false, users: false, others: false, tables: false, website: false, cloudSync: false },
  settingsCards: {},
  posOperations: { createOrders: true, completeOrders: false, processRefunds: false, viewReports: false, manageTables: false, manageReservations: false, manageCustomers: false, managePOSUsers: false, endOfDay: false },
};

function cleanValue(value: unknown): unknown {
  if (value === undefined) return undefined;
  if (value === null || typeof value !== "object") return value;
  if (value instanceof Timestamp || typeof (value as { toDate?: () => Date }).toDate === "function") return value;
  if (Array.isArray(value)) return value.map(cleanValue).filter((entry) => entry !== undefined);
  const next: Record<string, unknown> = {};
  Object.entries(value as Record<string, unknown>).forEach(([key, entry]) => {
    const cleaned = cleanValue(entry);
    if (cleaned !== undefined) next[key] = cleaned;
  });
  return next;
}

function storeSettings(data: Record<string, unknown>) {
  const next: Record<string, unknown> = {};
  Object.entries(data).forEach(([key, value]) => {
    if (DROP_KEYS.has(key) || /certificate|thumbprint|cloudMev/i.test(key)) return;
    const cleaned = cleanValue(value);
    if (cleaned !== undefined) next[key] = cleaned;
  });
  return next;
}

async function hashPin(pin: string) {
  const data = new TextEncoder().encode(pin);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(hashBuffer)).map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

function nextPin(used: Set<string>) {
  for (let attempt = 0; attempt < 30; attempt += 1) {
    const bytes = new Uint32Array(1);
    crypto.getRandomValues(bytes);
    const pin = String(bytes[0] % 1000000).padStart(6, "0");
    if (!used.has(pin)) {
      used.add(pin);
      return pin;
    }
  }
  throw new Error("A PIN could not be assigned.");
}

function displayName(name: string, email: string) {
  const trimmed = name.trim();
  if (trimmed) return trimmed;
  const local = email.trim().split("@")[0] || "";
  return local || "User";
}

async function writePosUser(storeId: string, name: string, pin: string, role: "admin" | "member", createdBy: string) {
  const ref = doc(collection(db, "stores", storeId, "posUsers"));
  const now = serverTimestamp();
  await setDoc(ref, {
    name,
    pin,
    pinHash: await hashPin(pin),
    role,
    permissions: role === "admin" ? adminPermissions : memberPermissions,
    isActive: true,
    createdBy,
    createdAt: now,
    updatedAt: now,
  });
  return ref.id;
}

async function organizationLink(ownerEmail: string, choice: OrganizationChoice) {
  if (choice.mode === "skip") return null;
  if (choice.mode === "existing") {
    if (!choice.id) throw new Error("Choose an organization.");
    return { [FIELD_BY_TYPE[choice.type]]: choice.id };
  }
  const name = choice.name.trim();
  if (!name) throw new Error("Enter an organization name.");
  const payload: Record<string, unknown> = {
    type: choice.type,
    name,
    createdBy: ownerEmail,
    admins: [ownerEmail],
    created: serverTimestamp(),
  };
  if (choice.address?.trim()) payload.address = choice.address.trim();
  if (choice.phone?.trim()) payload.phone = choice.phone.trim();
  if (choice.email?.trim()) payload.email = choice.email.trim();
  const ref = await addDoc(collection(db, "organizations"), payload);
  return { [FIELD_BY_TYPE[choice.type]]: ref.id };
}

export async function createStoreRecord(input: NewStoreInput): Promise<CreatedStorePins> {
  const ownerEmail = input.ownerEmail.trim();
  const name = input.name.trim();
  if (!ownerEmail) throw new Error("Sign in to create a store.");
  if (!name) throw new Error("Store name is required.");
  const adminEmail = input.adminEmail.trim().toLowerCase();
  const managerEmail = input.managerEmail.trim().toLowerCase();
  const adminName = displayName(`${input.adminFirstName} ${input.adminLastName}`, adminEmail);
  const managerName = displayName(`${input.managerFirstName} ${input.managerLastName}`, managerEmail);
  const ownerName = displayName("", ownerEmail);
  const admins = [ownerEmail];
  if (adminEmail && adminEmail !== ownerEmail.toLowerCase()) admins.push(adminEmail);

  let payload: Record<string, unknown> = {
    subscriptionPlan: "essential",
    subscriptionStatus: "active",
    orderLimit: 100,
    stationStartTime: "04:00",
    storeMode: "sandbox",
  };
  if (input.sourceStoreId) {
    const snap = await getDoc(doc(db, "stores", input.sourceStoreId));
    if (!snap.exists()) throw new Error("The store to clone was not found.");
    payload = storeSettings(snap.data() as Record<string, unknown>);
    payload.subscriptionPlan = "essential";
    payload.subscriptionStatus = "active";
    payload.storeMode = "sandbox";
  }

  payload.name = name;
  payload.address = input.address.trim();
  payload.phone = input.phone.trim();
  payload.email = input.email.trim();
  payload.currency = input.currency || "CAD";
  payload.storeType = "full";
  payload.billingSubactivity = input.billingSubactivity === "BAR" || input.billingSubactivity === "CDR" || input.billingSubactivity === "RES"
    ? input.billingSubactivity
    : (payload.billingSubactivity === "BAR" || payload.billingSubactivity === "CDR" ? payload.billingSubactivity : "RES");
  payload.createdBy = ownerEmail;
  payload.admins = admins;
  payload.members = managerEmail && managerEmail !== adminEmail ? [managerEmail] : [];
  payload.printerSettings = [createMevPrinterSetting(), createKitchenAllPrinter()];
  payload.created = serverTimestamp();
  payload.updated = serverTimestamp();
  const latitude = Number(input.latitude);
  const longitude = Number(input.longitude);
  if (Number.isFinite(latitude) && Number.isFinite(longitude) && input.latitude && input.longitude) {
    payload.locationLatitude = latitude;
    payload.locationLongitude = longitude;
  }
  const organization = await organizationLink(ownerEmail, input.organization);
  if (organization) payload.organization = organization;

  const storeRef = await addDoc(collection(db, "stores"), payload);
  let menuWarning = "";
  if (input.sourceStoreId) {
    try {
      const menus = await listStoreMenus(input.sourceStoreId);
      for (const menu of menus) {
        await copyMenu({
          sourceStoreId: input.sourceStoreId,
          menuId: menu.id,
          destinationStoreId: storeRef.id,
        });
      }
    } catch (error) {
      menuWarning = error instanceof Error ? error.message : "The menu copy stopped.";
    }
  }

  const used = new Set<string>();
  const ownerPin = nextPin(used);
  const adminPin = nextPin(used);
  const managerPin = nextPin(used);
  const ownerId = await writePosUser(storeRef.id, ownerName, ownerPin, "admin", ownerEmail);
  const adminId = await writePosUser(storeRef.id, adminName, adminPin, "admin", ownerEmail);
  const managerId = await writePosUser(storeRef.id, managerName, managerPin, "member", ownerEmail);
  const linked: Record<string, string> = { [ownerEmail]: ownerId };
  if (adminEmail) linked[adminEmail] = adminId;
  if (managerEmail) linked[managerEmail] = managerId;
  await setDoc(storeRef, { linkedPOSUserByEmail: linked }, { merge: true });

  return {
    storeId: storeRef.id,
    storeName: input.name.trim(),
    ownerPin,
    ownerName,
    ownerEmail,
    adminPin,
    adminName,
    adminEmail,
    managerPin,
    managerName,
    managerEmail,
    menuWarning,
  };
}
